import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContentAreaComponent } from "src/app/components/content-area/content-area.component";
import { TableComponent } from "src/app/components/table/table.component";
import { IActionClick, IPaginateEvent, ISortChange } from 'src/app/interfaces/components/table.interface';
import { SelectComponent } from "src/app/components/select/select.component";
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from "src/app/components/input/input.component";
import { Router } from '@angular/router';
import { TContentAreaAction } from 'src/app/interfaces/components/content-area.interface';
import { FilterAreaComponent } from "src/app/components/filter-area/filter-area.component";
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import {
  contentAreaActions,
  displayedColumns,
  personTypeOptions,
  tableActions,
  tableLegends,
} from './clients-list.constants';
import { statusOptions } from 'src/app/shared/constants/status-select.constants';
import { ClientsInfoComponent } from '../clients-info/clients-info.component';
import { ClientsService } from 'src/app/services/clients.service';
import { AlertService } from 'src/app/services/alert.service';
import { IClient } from 'src/app/interfaces/clients/client.interface';
import { TFilterAction } from 'src/app/interfaces/components/filter.interface';
import { ExportService } from 'src/app/services/export.service';
import { PersonService } from 'src/app/services/person/person.service';

@Component({
  selector: 'app-clients-list',
  standalone: true,
  imports: [
    ContentAreaComponent,
    TableComponent,
    TranslateModule,
    SelectComponent,
    InputComponent,
    FilterAreaComponent,
    ReactiveFormsModule
  ],
  templateUrl: './clients-list.component.html'
})
export class ClientsListComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly clientsService = inject(ClientsService);
  private readonly alertService = inject(AlertService);
  private readonly exportService = inject(ExportService);
  private readonly personService = inject(PersonService);

  contentAreaActions = contentAreaActions;
  statusOptions = statusOptions;
  dataList: IClient[] = [];
  tableLegends = tableLegends;
  displayedColumns = displayedColumns;
  personTypeOptions = personTypeOptions;
  tableActions = tableActions;
  length = 0;

  form: FormGroup = this.clientsService.formFilterList;

  get filterPayload() {
    return {
      document: this.form.get('document')?.value,
      name: this.form.get('name')?.value,
      isHeadOffice: this.form.get('isHeadOffice')?.value,
      status: this.form.get('status')?.value,
      page: this.form.get('page')?.value,
      pageSize: this.form.get('pageSize')?.value,
      sortBy: this.form.get('sortBy')?.value,
      sortOrder: this.form.get('sortOrder')?.value,
    };
  }

  get pageIndex() {
    return this.form.get('page')?.value;
  }

  get pageSize() {
    return this.form.get('pageSize')?.value;
  }

  ngOnInit(): void {
    this.getTableData();
  }

  handleFilterAreaEvent(event: TFilterAction) {
    if (event === 'cleanFilters') {
      this.form.reset();
    } else if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.handlePaginatorClick();
  }

  onSortChange(event: ISortChange) {
    this.form.get('sortBy')?.setValue(event.sortField);
    this.form.get('sortOrder')?.setValue(event.sortDirection);
    this.getTableData();
  }

  handlePaginatorClick(event?: IPaginateEvent) {
    if (event) {
      this.form.get('pageSize')?.setValue(event.pageSize);
      this.form.get('page')?.setValue(event.page);
    } else {
      this.form.get('pageSize')?.setValue(5);
      this.form.get('page')?.setValue(1);
    }
    this.getTableData();
  }

  getTableData() {
    const payload = this.filterPayload;

    this.clientsService.listAll(payload).subscribe({
      next: res => {
        const dataList = res.items.map((item: any) => {
          return {
            ...item,
            tableStatus: item.status ? 'Ativo' : 'Inativo',
            isHeadOffice: item.isHeadOffice ? 'Matriz' : 'Filial',
          };
        });
        this.dataList = dataList;
        this.length = res.totalItems;
      },
      error: (error) => {
        this.alertService.show({
          type: 'error',
          subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_TABLE_MESSAGE_ERROR'
        });
      },
    });
  }

  handleContentAreaEvent(event: TContentAreaAction) {
    if (event === 'add') {
      this.router.navigate(['/panel-master/clients/form']);
    }
    if (event === 'export') {
      this.export();
    }
  }

  handleEmitterEventActionClick(event: IActionClick<IClient>){
    const id = event.row.id;
    const name = event.row.name;

    if (event.type === 'edit') {
      this.router.navigate([`/panel-master/clients/form/${id}`]);
    }

    if (event.type === 'view') {
      this.view(id);
    }

    if (event.type === 'delete') {
      this.delete(name, id);
    }

    if (event.type === 'toggle') {
      this.toggle(event.row);
    }
  }

  export() {
    const payload = this.filterPayload;

    this.clientsService.export(payload).subscribe({
      next: (blob) => {
        this.exportService.downloadBlob(
          blob,
          'clientes.xlsx'
        );
      },
      error: (error) => {
        this.alertService.show({
          type: 'error',
          subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
        });
      }
    });
  }

  toggle(row: IClient) {
    let subtitle = '';
    const name = row.name;

    if (row.status === true) {
      subtitle = this.translate.instant('CLIENTS_LIST.MODAL.SUBTITLE_TOGGLE_INACTIVE', { name });
    } else {
      subtitle = this.translate.instant('CLIENTS_LIST.MODAL.SUBTITLE_TOGGLE_ACTIVE', { name });
    }

    this.dialog.open(ModalComponent, {
      data: {
        type: 'toggle',
        subtitle: subtitle,
      },
    })
    .afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.personService.toggle(row.id, !row.status).subscribe({
          next: () => {
            this.getTableData();
          },
          error: (error) => {
            this.alertService.show({
              type: 'error',
              subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
            });
          }
        });
      }
    });
  }

  delete(name: string, id: string) {
    const subtitle = this.translate.instant('CLIENTS_LIST.MODAL.SUBTITLE_DELETE', { name });

    this.dialog.open(ModalComponent, {
      data: {
        type: 'delete',
        subtitle: subtitle,
      },
    })
    .afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.personService.delete(id).subscribe({
          next: () => {
            this.getTableData();
          },
          error: (error) => {
            this.alertService.show({
              type: 'error',
              subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
            });
          }
        });
      }
    });
  }

  view(id: string) {
    this.dialog.open(ClientsInfoComponent, {
      data: {
        id: id,
      },
    })
  }
}
